import 'allsettled-polyfill';
import { initialize as initializeFeed } from '@finn-no/feed';
import { writeExperimentData } from '@finn-no/unleash-cookie/unleash-cookie.js';

import { debouncedLoadTags, pushToAnqSafe } from './podlet/ast';
import { HorseshoeConfig } from '../shared/types/podlet';
import {
    verticalSpecificContentMarketing,
    verticalSpesificInit,
} from './podlet/vertical';
import { Apntag, BannerGlobal, Finn } from '../shared/types/ast';
import { requestBanners } from './utils/banners';
import { renderHorseshoe } from './components/horseshoe/horseshoe-render';
import { renderSkyscraper } from './components/skyscraper/skyscraper-render';

import { version } from '../shared/version';
import diagnostics from './utils/diagnostics';
import {
    markHorseshoeInitiated,
    markHorseshoeLoaded,
} from './utils/performance';

import { renderBoard } from './components/board/board-render';
import { renderContentBoard } from './components/content-board/content-board-render';
import { loadGlimrIfPermission } from './utils/glimr';
import { persistPPID } from './utils/pulse-ppid';
import { getRefreshState } from './refresh-state';
import { displayAdsLog } from './utils/debug';
import { setupRecirNativeAdSubscription } from './components/recirc-native-ad/recirc-native-ad-render';
import { getConfig } from './config';
import { renderLocalTestEnvironment } from './components/localhost/render-localhost-render';
import { renderContentMarketing } from './components/content-marketing/content-marketing-render';
import { setupRecirBannerAdSubscription } from './components/recirc-banner-ad/recirc-banner-ad-render';
import { companionReadyPlacementListener } from './utils/companion-ready-placement-listener';
import { setupAfs } from './utils/google-afs';

const refreshState = getRefreshState();

window.FINN = window.FINN || ({} as Finn);
window.FINN.banner = window.FINN.banner || ({} as BannerGlobal);
window.FINN.banner.queue = window.FINN.banner.queue || [];
window.FINN.banner.debouncedLoadTags =
    window.FINN.banner.debouncedLoadTags || debouncedLoadTags();

window.apntag = window.apntag || ({} as Apntag);
window.apntag.anq = window.apntag.anq || [];

export function copyQueue() {
    const { queue } = window.FINN.banner;
    const tempQueue = Array.isArray(queue) ? [...queue] : [];

    if (window.apntag) {
        window.FINN.banner.queue = window.apntag.anq;
        const { anq } = window.apntag;
        tempQueue.forEach((element) => anq.push(element));
    }
}

async function loadConfig(config: HorseshoeConfig) {
    refreshState.clientReady = !config.waitForClient;

    // Check if object is not empty
    if (
        config?.experimentsAsJson &&
        Object.keys(config?.experimentsAsJson).length !== 0
    ) {
        writeExperimentData(config.experimentsAsJson);
    }

    // persist PPID data
    // NB! A new await here might be a terrible idea
    Promise.all([persistPPID(), loadGlimrIfPermission(config)]).then(() => {
        // PPID and Glimr is set
    });

    const { xandr } = config;

    refreshState.setInitialData(config);

    if (xandr && xandr.pageOpts) {
        pushToAnqSafe(() => {
            requestBanners(
                window.apntag,
                refreshState.getXandrRequestData(),
                true,
                true,
            );
            if (refreshState.clientReady) {
                displayAdsLog('index.ts: client ready > copy queue');
                copyQueue();
            }
        });

        refreshState.setupSubscriptions(config);

        initializeFeed();

        const props = {
            deviceType: config.deviceType,
            vertical: config.vertical,
            pageType: config.pageType,
            wideHorseshoe: config.wideHorseshoe || false,
        };

        if (!config.configOnly) {
            if (config.skyscraperOnly) {
                displayAdsLog(`index.ts render skyscraperOnly`);
                renderSkyscraper('horseshoe-root', props);
            } else {
                displayAdsLog(`index.ts render horseshoe`);
                renderHorseshoe('horseshoe-root', props);
            }
        }

        const isObject = config.pageType === 'object';

        if (isObject || config.pageType === 'front') {
            setupRecirNativeAdSubscription();
            setupRecirBannerAdSubscription();
        }

        if (config.pageType === 'result') {
            setTimeout(() => {
                companionReadyPlacementListener(config.placementInstructions);
            }, 500);

            setupAfs();
        }

        if (isObject) {
            let hasBoard1 = false;
            let hasContentboard = false;

            config?.placements?.forEach((placement) => {
                if (placement?.placementId === 'board_1') hasBoard1 = true;
                if (placement?.placementId === 'contentboard')
                    hasContentboard = true;
            });

            if (hasBoard1) {
                renderBoard('board_1', props);
            }

            if (hasContentboard) {
                renderContentBoard('contentboard', {
                    ...props,
                    placementId: 'contentboard',
                });
            }
        }

        /**
         * Loads native components in local.finn.no:3040
         */
        renderLocalTestEnvironment('localhost-root');

        verticalSpesificInit(config);
        verticalSpecificContentMarketing(config);
    }
}

function initConditionalRender() {
    const config = getConfig();
    const { contentMarketingOnly } = config;
    if (contentMarketingOnly) {
        renderContentMarketing('content-marketing');
    } else {
        initPodlet(config);
    }
}

function initPodlet(config: HorseshoeConfig) {
    markHorseshoeInitiated();

    if (config.version === version) {
        diagnostics.groupCollapsed(
            'displayads-podlet-horseshoe loading version: %s ',
            version,
        );
        diagnostics(config);
        diagnostics.groupEnd();
    } else {
        diagnostics(
            `Server version (${config.version}) does not match client version (${version})`,
        );
    }

    loadConfig(config);
}

markHorseshoeLoaded();

window.addEventListener('load', initConditionalRender);
